<script lang="ts">
import { InputText, UiPreview4 } from '#components'

export function countryEqual(prev: gen.BuyerAddressCountryFragment, next: gen.BuyerAddressCountryFragment): boolean {
  return prev?.iso2 === next?.iso2
}
</script>

<script setup lang="ts">
const t2 = useT2({
  Lithuania: 'Lietuva',
  Africa: 'Afrika',
  Antarctica: 'Antarktida',
  Asia: 'Azija',
  Europe: 'Europa',
  'North America': 'Šiaurės Amerika',
  Oceania: 'Okeanija',
  'South and Central America': 'Pietų ir Centrinė Amerika',
  Country: 'Šalis',
  'Country region': 'Šalies regionas',
  Mainland: 'Žemyninė dalis',
  'Please select your region': 'Prašome pasirinkti savo regioną',
  'Please update country region': 'Prašome atnaujinti šalies regioną',
  Region: 'Regionas',
})

const props = defineProps<{
  label?: stringI18n
  optional?: boolean
  novalidate?: boolean
  in: gen.BuyerAddressCountryFragment | None
  out?: ((value: gen.InputBuyerAddressCountry) => void) | None
}>()

const previewElem = useTemplateRef('preview')
const inputElem = useTemplateRef('input')
const inited = useInited('init3')

type State = 'none' | 'country'

const data = shallowReactive({
  showCountries2: false,
  state: 'none' satisfies State as State,
  countriesInit: false,
  showMinimap: false,
  query: '',
})

const timerBlurSelectFirst = useTimeout()

const [holder, _holderSet] = useValueSynced2(
  (): {
    iso2: string | None
  } => {
    const item = props.in
    return {
      iso2: item?.iso2 || undefined,
    }
  }
)

function holderSet(...args: Parameters<typeof _holderSet>) {
  timerBlurSelectFirst.clear()
  _holderSet(...args)
}

const { country: countryFull } = useCountriesMultiget(() => ({ iso2: props.in?.iso2 }))

function stateSet(inp: State) {
  if (!data.countriesInit) data.countriesInit = true
  data.state = inp
  data.showCountries2 = inp === 'country'
}

const { isLoading, searchOn, list: countryList } = useCountrySearch(() => ({ enabled: data.countriesInit }))

function findCountry(iso2: string | None) {
  return helpers.arrFindId(countryList.value, iso2, (i) => i.iso2) || (countryFull.value?.iso2 === iso2 ? countryFull.value : undefined)
}

const timerCountries = useTimeout()
watch(
  () => ({ items: countryList.value, iso2: holder.value.iso2, one: countryFull.value }),
  ({ iso2 }) =>
    timerCountries.set(() => {
      const name = findCountry(iso2)?.title.value
      if (name) data.query = name
    }, 50),
  { immediate: true }
)

const { items: countrySearch } = useWeSearch2({
  query: () => data.query,
  items: () => countryList.value,
  searchOn: (i) => searchOn(i),
})

const ui = computed(() => {
  const value = holder.value
  const country = findCountry(value?.iso2)

  return {
    country,
    iso2: country?.iso2 || value?.iso2,
    showPreview: data.state == 'country' && (isLoading.value || !!countrySearch.value.length),
  }
})

const timerMinimap = useTimeout()
watch(
  () => ui.value.showPreview,
  (on) => timerMinimap.set(() => (data.showMinimap = on), on ? 300 : 0)
)

watch(
  () => ui.value.showPreview || data.showMinimap,
  (on) => (data.showCountries2 = on),
  { immediate: true }
)

const showCountries = computed({
  get: (): boolean => data.showCountries2,
  set: () => stateSet('none'),
})

function formOut(): gen.InputBuyerAddressCountry | undefined {
  const country = findCountry(holder.value.iso2)
  return country?.iso2 ? { iso2: country.iso2 } : undefined
}

const timer = useTimeout()
function emitChanged() {
  timer.set(() => {
    const out = formOut()
    const prev = props.in && 'iso2' in props.in ? props.in : null
    if (out && (!prev || !countryEqual(out, prev))) props.out?.(out)
  }, 10)
}

function countrySet(iso2: string) {
  const country = findCountry(iso2)
  if (!country?.title?.value) return
  holderSet({ iso2: country.iso2 })
  stateSet('none')
  emitChanged()
}

function clear() {
  data.query = ''
  holderSet({ iso2: undefined })
  inputElem.value?.focus()
  stateSet('country')
}

// // continent
// const continentT = useEnumTranslator<NN<MarketCountryFragment['continent']>>(() => ({
//   Af: t2('Africa'),
//   An: t2('Antarctica'),
//   As: t2('Asia'),
//   Eu: t2('Europe'),
//   Na: t2('North America'),
//   Oc: t2('Oceania'),
//   Sa: t2('South and Central America'),
// }))

// const colors = {
//   Af: 'black',
//   An: 'white',
//   As: 'yellow',
//   Eu: 'blue',
//   Na: 'red',
//   Oc: 'green',
//   Sa: 'pink',
// } satisfies Record<NN<MarketCountryFragment['continent']>, Color>

// const continentActive = ref<MarketCountryFragment['continent']>()

function toId(iso2: string) {
  return `alpha:${iso2}`
}

const countrySearch2 = computed(() => {
  const alhabet: string[] = []
  // const cont = continentActive.value
  const all = helpers
    .arr(countrySearch.value, (item) => {
      // if (cont && item.continent !== cont) return
      const alhpa = item.title.value?.at(0)?.toLocaleUpperCase('en') || ''
      const isFirstAlhpa = !!alhpa && !alhabet.includes(alhpa)
      if (alhpa && isFirstAlhpa) alhabet.push(alhpa)
      return {
        key: item.iso2,
        item,
        alhpa,
        id: isFirstAlhpa ? toId(alhpa) : undefined,
      }
    })
    .sort((a, b) => a.alhpa.localeCompare(b.alhpa))

  const topCountries = helpers.arr(all, (item): (typeof all)[0] | None => {
    if (!item.item.priority) return
    return { item: item.item, alhpa: '', id: undefined, key: `top:${item.item.iso2}` }
  })

  return {
    alhabet: alhabet.sort((a, b) => a.localeCompare(b)),
    items: [...topCountries, ...all],
    scrollTo(iso2: string) {
      const id = toId(iso2)
      const node = document.getElementById(id)
      const el = helpers.getHTMLElement(previewElem.value?.elemScroll())
      if (!el || !node) return
      el.scrollTo({ top: myElem(node, 'offsetTop', 0), behavior: 'smooth' })
    },
  }
})

const myInput = useMyInput<gen.InputBuyerAddressCountry>({
  label: () => props.label,
  optional: () => props.optional,
  value: () => helpers.required(formOut(), 'Missing country'),
})

defineExpose({
  ...myInput.expose(),
  outIsEu() {
    return computed(() => !!helpers.arrFindId(countryList.value, ui.value.iso2, (i) => i.iso2)?.isEu)
  },
  out() {
    return helpers.required(formOut(), 'Missing country')
  },
})

const placeholder = computed(() => t2('Lithuania'))

function selectFirts() {
  const iso2 = countrySearch2.value.items.at(0)?.item.iso2
  if (iso2) countrySet(iso2)
}
function enter(e: unknown) {
  if (e && e instanceof KeyboardEvent && 'key' in e) {
    if (e.key === 'Enter' || e.key === 'Tab') selectFirts()
    else if (!data.showCountries2) data.showCountries2 = true
  }
}

function focus(e: boolean) {
  if (e) stateSet('country')
  else timerBlurSelectFirst.set(() => selectFirts(), 500)
}
</script>

<template>
  <div class="_col _gap1">
    <InputText
      ref="input"
      v-model="data.query"
      type="text"
      :placeholder
      :label="t2('Country')"
      @mouseenter="() => (data.countriesInit = true)"
      @keydown="(e: unknown) => enter(e)"
      @focus="(e) => focus(e)"
    >
      <template #after>
        <div v-show="ui.iso2" :class="$style.after">
          <UiCountryImg :country="ui.country" />
          <ButtonIcon :txt="t2('Remove')" size="36" @click="() => clear()" />
        </div>
      </template>
    </InputText>

    <UiPreview4 v-if="inited || showCountries" ref="preview" v-model="showCountries" dropdown scroll :elem="() => inputElem" @click="() => timerBlurSelectFirst.clear()">
      <template v-if="isLoading">
        <ButtonSelect v-for="nr in 10" :key="nr" :on="false" :click="undefined">
          <UiLoading />
        </ButtonSelect>
      </template>

      <template v-for="(item, index1) in countrySearch2.items" :key="item.key">
        <div v-if="item.id" :id="item.id" :class="$style.alpha">{{ item.alhpa }}</div>
        <ButtonSelect :on="holder.iso2 === item.item.iso2 || index1 === 0" :click="() => countrySet(item.item.iso2)">
          <UiCountryImg :iso2="item.item.iso2" :country="item.item" />
          <span>{{ item.item.title.value }}</span>
        </ButtonSelect>
      </template>

      <template v-if="data.showMinimap" #floating>
        <div :class="$style.minimap" @click="() => timerBlurSelectFirst.clear()">
          <div :class="$style.minimap2">
            <button v-for="a in countrySearch2.alhabet" :key="a" type="button" :class="$style.minimapB" @click.passive="countrySearch2.scrollTo(a)">{{ a }}</button>
          </div>
        </div>
      </template>
    </UiPreview4>
  </div>
</template>

<style module>
.minimap {
  composes: global(_absolute);
  composes: global(_hidden);
  composes: global(_br22);
  composes: global(_col);
  composes: global(_h100p);
  composes: global(_w40);

  right: 0;
  top: 0;

  composes: global(_stretch);
  composes: global(_end);
  z-index: 9;
}
.minimap2 {
  composes: global(_col);
  composes: global(_h100p);
  composes: global(_scroll);
  composes: global(_sticky);
  composes: global(_bg0);
  bottom: 0;
}

.minimapB {
  composes: global(_ctr);
  composes: global(_fs07);
  composes: global(_fw-xl);
  composes: global(_h20);

  composes: global(_cb3);
  flex: 1 0 auto;
  max-height: 40px;
}

.minimapB:hover {
  color: var(--blue2);
  background: var(--black-5);
}

.alpha {
  composes: global(_fw-xl);
  composes: global(_fs11);

  composes: global(_pt04);
  composes: global(_pr1);
  composes: global(_pb04);
  composes: global(_pl1);

  composes: global(_c1);
}

.after {
  composes: global(_absolute);
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_gap1);
  composes: global(_h100p);

  top: 0;
  bottom: 0;
  right: 4px;
  z-index: 5;
}
</style>
